import React, { FC, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { WorkDetailsModel } from "../../../../factories/work/model/work-details.model";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useAddAutomationMutation,
  useDeleteAutomationMutation,
  useDeleteJobTaskAutomationMutation,
  useGetAutomationConditionsQuery,
  useGetAutomationJobTaskConditionsQuery,
  useGetAutomationsQuery,
} from "../../../../slices/AutomationSlice";
import { AddAutomationModal } from "./AddAutomationModal";
import { AutomationDraft, AutomationItem } from "./automation.constants";
import {
  getAutomationActionBodyValue,
  getAutomationConditionBodyValue,
} from "./automation.helpers";
import { mapAutomationsResponseToItems } from "./automation.mappers";
import { AutomationTarget } from "./automation.types";
import { AutomationList } from "./AutomationList";
import { useAutomationLookups } from "./useAutomationLookups";

type Props = {
  work: WorkDetailsModel;
};

export const Automation: FC<Props> = ({ work }) => {
  const user = useAppSelector((state) => state.appReducer.user);
  const lookups = useAutomationLookups(work.serviceId);
  const [addAutomation, { isLoading: isAddingAutomation }] =
    useAddAutomationMutation();
  const [deleteAutomation] = useDeleteAutomationMutation();
  const [deleteJobTaskAutomation] = useDeleteJobTaskAutomationMutation();
  const [selectedTarget, setSelectedTarget] = useState<AutomationTarget | null>(
    null,
  );
  const isAddingAutomationRef = useRef(false);
  // Deleting asks first (Roadmap 1815).
  const [confirmDelete, setConfirmDelete] = useState<AutomationItem | null>(
    null,
  );
  const deletingAutomationIdsRef = useRef<Set<string>>(new Set());
  const [deletingAutomationIds, setDeletingAutomationIds] = useState<
    Set<string>
  >(() => new Set());

  const {
    data: automationsData,
    isFetching: isLoadingAutomations,
    isError: isAutomationsError,
  } = useGetAutomationsQuery(
    { workId: work.id },
    { refetchOnMountOrArgChange: true },
  );
  const { data: workConditions = [] } = useGetAutomationConditionsQuery();
  const { data: jobTaskConditions = [] } =
    useGetAutomationJobTaskConditionsQuery();

  const createdBy = useMemo(() => {
    const fullName = `${user?.userProfile?.firstName || ""} ${
      user?.userProfile?.lastName || ""
    }`.trim();

    return fullName || "Unknown user";
  }, [user?.userProfile?.firstName, user?.userProfile?.lastName]);

  const workTarget = useMemo<AutomationTarget>(() => {
    return {
      type: "work",
      id: work.id,
      title: work.name,
      work,
    };
  }, [work]);

  const visibleAutomations = useMemo(
    () =>
      mapAutomationsResponseToItems({
        data: automationsData,
        workName: work.name,
        options: lookups.options,
        workConditions,
        jobTaskConditions,
      }),
    [
      automationsData,
      jobTaskConditions,
      lookups.options,
      work.name,
      workConditions,
    ],
  );

  const handleOpenWorkModal = () => {
    setSelectedTarget(workTarget);
  };

  const handleCloseModal = () => {
    setSelectedTarget(null);
  };

  const handleAddAutomation = async (draft: AutomationDraft) => {
    if (isAddingAutomationRef.current) {
      return;
    }

    if (!draft.conditionId || !draft.actionId) {
      return;
    }

    const orgId = work.orgId || getLinkedFirm()?.orgId || "";

    if (!orgId || !user?.id) {
      toast.error("Missing firm or user for automation");
      return;
    }

    const conditionValue = getAutomationConditionBodyValue({
      conditionKey: draft.conditionKey,
      conditionValue: draft.conditionValue,
      conditionExtraValue: draft.conditionExtraValue,
      workStatusOptions: lookups.options.workStatuses,
      subtaskStatusOptions: lookups.options.subtaskStatuses,
    });
    const actionValue = getAutomationActionBodyValue({
      actionKey: draft.actionKey,
      actionValue: draft.actionValue,
      actionExtraValue: draft.actionExtraValue,
      workStatusOptions: lookups.options.workStatuses,
      subtaskStatusOptions: lookups.options.subtaskStatuses,
    });

    isAddingAutomationRef.current = true;

    try {
      await addAutomation({
        orgId,
        workId: work.id,
        createdById: user.id,
        conditionId: draft.conditionId,
        actionId: draft.actionId,
        isActive: true,
        isExecuted: false,
        isSuccess: false,
        conditionValue,
        actionValue,
      }).unwrap();
    } catch {
      toast.error("Failed to create automation");
      return;
    } finally {
      isAddingAutomationRef.current = false;
    }

    setSelectedTarget(null);
    toast.success("Automation created successfully");
  };

  const handleDeleteAutomation = async (automation: AutomationItem) => {
    const automationId = automation.id;

    if (deletingAutomationIdsRef.current.has(automationId)) {
      return;
    }

    deletingAutomationIdsRef.current.add(automationId);
    setDeletingAutomationIds(new Set(deletingAutomationIdsRef.current));

    try {
      const deleteMutation =
        automation.scope === "subtask"
          ? deleteJobTaskAutomation
          : deleteAutomation;

      await deleteMutation(automationId).unwrap();
      toast.success("Automation deleted successfully");
    } catch {
      toast.error("Failed to delete automation");
    } finally {
      deletingAutomationIdsRef.current.delete(automationId);
      setDeletingAutomationIds(new Set(deletingAutomationIdsRef.current));
    }
  };

  return (
    <div className={"w-full p-6"}>
      <div className={"mb-6 flex items-center justify-between gap-4"}>
        <div>
          <h2 className={"m-0 text-[22px] font-bold text-[#253747]"}>
            Automation
          </h2>
        </div>

        <button
          type={"button"}
          className={
            "flex h-[32px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm font-semibold text-white disabled:opacity-75"
          }
          onClick={handleOpenWorkModal}>
          Add automation
        </button>
      </div>

      <AutomationList
        automations={visibleAutomations}
        workName={work.name}
        options={lookups.options}
        isLoading={isLoadingAutomations}
        isError={isAutomationsError}
        deletingAutomationIds={deletingAutomationIds}
        onDelete={setConfirmDelete}
      />

      {confirmDelete && (
        <SettingsConfirmModal
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            const automation = confirmDelete;

            setConfirmDelete(null);
            handleDeleteAutomation(automation);
          }}
          icon={<TrashSvg className={"h-5 w-5"} />}
          title={"Delete this automation?"}
          description={"Do you want to permanently delete this automation?"}
          confirmText={"Delete"}
          variant={"danger"}
        />
      )}

      <AddAutomationModal
        isOpen={Boolean(selectedTarget)}
        target={selectedTarget}
        createdBy={createdBy}
        lookups={lookups}
        isSaving={isAddingAutomation}
        onClose={handleCloseModal}
        onSave={handleAddAutomation}
      />
    </div>
  );
};
